import { useContext, useEffect, useState } from 'react';
import { Box, Chip, IconButton, Skeleton } from '@mui/material';
import { OAStatusChip, OATypography } from '../../../components/widgets';
import { ReactComponent as ArrowBackIcon } from '../../../assets/icons/arrow_back.svg';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Col,
  Dropdown,
  Row
} from 'react-bootstrap';
import { CustomDropdownItem } from '../../order-management/order-view/OrderActions';
import OAButton from '../../../components/widgets/OAButton';

import { ReactComponent as DotSeparatorIcon } from '../../../assets/icons/dot_separator.svg';
import { ReactComponent as CalenderIcon } from '../../../assets/icons/calender.svg';
import { ReactComponent as MembersIcon } from '../../../assets/icons/members.svg';
import { getFormattedDateRange, showDialog } from '../../../helper';
import classNames from 'classnames';
import ActivityLogs from '../HotelTabs/ActivityLogs';
import CommentsAndTasks from '../../order-management/order-view/CommentsAndTasks';
import TravellerInfo from '../HotelTabs/TravellerInfo';
import PaymentSummary from '../HotelTabs/PaymentSummary';
import { CancelHotelBooking } from '../CancelBooking';
import { FLIGHT_STATUS } from '../../../constants';
import { useAuth } from '../../../auth';
import { handleDownload } from '../../../helper/handleDownload';
import { PaymentStatus } from '../PaymentStatus';
import HotelOrderStatus from './HotelOrderStatus';
import HotelContext from '../context/HotelContext';
import HotelDetail from '../HotelTabs/HotelDetail';

export const HotelOrderView = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { getOrderById, statusChange, downloadInvoice, downloadVoucher } = useContext(HotelContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [docLoad, setDocLoad] = useState<boolean>(false);
  const [voucherLoad, setVoucherLoad] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isStatusUpdateOpen, setIsStatusUpdateOpen] = useState<boolean>(false);
  const params = useParams<{ id: any }>();
  const [selected, setSelected] = useState<string>('all');
  const [hotelData, setHotelData] = useState<any>();



  let hotelList = () => {
    getOrderById(params ? params?.id : null, res => {
      setHotelData(res);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    hotelList();
  }, [statusChange]);

  useEffect(() => {
    if (window.location.pathname?.includes('payment-summary')) {
      setPaymentStatus(true);
    }
  }, []);

  const membersCount = hotelData?.contactDetails?.id ? 1 : hotelData?.travellers?.length;
  let memberName = hotelData?.contactDetails?.id ? hotelData?.contactDetails?.fullName : hotelData?.travellers?.[0]?.fullName;
  const travelDuration: any =
    getFormattedDateRange(hotelData?.checkInDate, hotelData?.checkOutDate);

  const renderText = (text: string, width: number) => (
    <OATypography
      variant='body2'
      color={theme => theme?.palette?.grey?.[700]}
      isLoading={isLoading}
      skeletonProps={{ height: 24, width }}>
      {text}
    </OATypography>
  );

  const handleBack = () => {
    navigate(`/apps/hotels`);
  };

  const getClassNames = (pathValue: string) => {
    return classNames('nav-link text-active-primary me-4', {
      active: location?.pathname?.includes(pathValue)
    });
  };

  const handleClick = (link: string) => {
    if (link === 'payment-summary') {
      setPaymentStatus(true);
    } else {
      setPaymentStatus(false);
    }
  };
  const getListItem = (
    title: string,
    to: string,
    icon?: JSX.Element,
    iconPosition?: 'pre' | 'post'
  ) => (
    <li className='nav-item' onClick={() => handleClick(to)}>
      <Link to={`${to}${location?.search}`} className={getClassNames(to)}>
        {icon && iconPosition === 'pre' && icon}
        <span>{title}</span>
        {icon && iconPosition === 'post' && icon}
      </Link>
    </li>
  );

  const statusObj: any = FLIGHT_STATUS?.find(
    (obj: any) => obj?.value === hotelData?.bookingStatus
  );
  const isBookingValid = ['BOOKING_FAILED'].includes(hotelData?.bookingStatus) === false;
  const handleRecieptDownload = () => {
    const convenienceFee = hotelData?.orderAmount?.convenienceFee ?? 0;
    if (convenienceFee === 0) {
      showDialog({
        title: 'Cannot Download Invoice',
        text: 'Invoice can\'t be downloaded as convenience fee is 0',
        icon: 'error',
        showCancelButton: false,
        confirmButtonText: 'OK'
      });
      return;
    }
    setDocLoad(true);
    downloadInvoice(params ? params?.id : null, res => {
      if (res?.invoice) {
        handleDownload(res?.invoice?.url);
      }
      setDocLoad(false);
    });
  };
  const handleVoucherDownload = () => {
    setVoucherLoad(true);
    if(hotelData?.downloadAttachment?.voucher?.url){
      handleDownload(hotelData?.downloadAttachment?.voucher?.url);
      setVoucherLoad(false);
      return;
    }
    downloadVoucher(params ? params?.id : null, res => {
      if (res?.voucher) {
        handleDownload(res?.voucher?.url);
      }
      setVoucherLoad(false);
    });
  };
  const counts = hotelData?.travellerRoom.reduce(
    (totals: { adult: any; child: any; infant: any; }, room: { adult: any; child: any; infant: any; }) => {
        totals.adult += room.adult;
        totals.child += room.child;
        totals.infant += room.infant;
        return totals;
    },
    { adult: 0, child: 0, infant: 0 }
  );
  return (
    <>
      <Routes>
        <Route
          element={
            <>
              <Row className='order-view'>
                <Col md='12' className='mb-4'>
                  <Card>
                    <Box display='flex' justifyContent='space-between' padding='16px 16px 0px 16px'>
                      <Box
                        display='flex'
                        alignItems='center'
                        sx={{ cursor: 'pointer' }}
                        onClick={handleBack}>
                        <IconButton aria-label='go back' size='medium'>
                          <ArrowBackIcon />
                        </IconButton>
                        <OATypography variant='subtitle1'>
                          Back to hotel order managment
                        </OATypography>
                      </Box>
                      {hotelData?.bookingStatus !== 'ITINERARY' && (
                        <Box display='flex' gap='1rem' alignItems='center'>
                          <Dropdown align='end'>
                            <Dropdown.Toggle className='btn-sm btn-secondary'>
                              Manage order
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {hotelData?.bookingStatus !== 'CANCELLED' && (
                                <CustomDropdownItem
                                  key='cancel_hotel'
                                  label='Cancel Hotel'
                                  error
                                  handler={() => setIsCancelModalOpen(true)}
                                />
                              )}
                              {/* <CustomDropdownItem
                              key='date_change'
                              label='Date Change'
                              handler={() => }
                            /> */}
                              {
                                <CustomDropdownItem
                                  key='update_status'
                                  label='Update Status'
                                  handler={() => setIsStatusUpdateOpen(true)}
                                />
                              }
                            </Dropdown.Menu>
                          </Dropdown>
                          {isBookingValid && (
                            <OAButton
                              label={`Download tax receipt`}
                              onClick={handleRecieptDownload}
                              isLoading={docLoad}
                              loadingText='downloading...'
                              className='btn btn-light-primary'
                            />
                          )}
                          {isBookingValid && (
                            <OAButton
                              label='Download Voucher'
                              onClick={handleVoucherDownload}
                              isLoading={voucherLoad}
                              loadingText='downloading...'
                              className='btn btn-light-primary'
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Card.Body className='py-0'>
                       <Box display='flex' alignItems='center' margin='24px 0px 8px 0px' gap='8px'>
                          {isLoading ? (
                            <Skeleton variant='text' width={40} height={20} />
                          ) : (
                            <OATypography variant='h2'>
                              {hotelData?.hotelDetails?.hotelName ?? "--"}
                            </OATypography>
                          )}
                          {isLoading ? (
                            <Skeleton variant='text' width={50} height={20} />
                          ) : (
                            <OAStatusChip
                              status={statusObj?.value}
                              label={statusObj?.label}
                              variant={statusObj?.type}
                            />
                          )}
                        </Box>
                      <Box display='flex' flexDirection='column' gap='8px'>
                        <Box display='flex' alignItems='center' marginBottom='10px'>
                          {hotelData?.bookingId ? renderText(hotelData?.bookingId, 90) : null}
                          {hotelData?.bookingId && travelDuration && (
                            <DotSeparatorIcon style={{ margin: '0 6px' }} />
                          )}{' '}
                          <Box display='flex' alignItems='center' gap='4px'>
                            <CalenderIcon />
                            {travelDuration ? renderText(travelDuration, 186) : null}
                          </Box>
                          <DotSeparatorIcon style={{ margin: '0 6px' }} />
                          <Box display='flex' alignItems='center' gap='4px'>
                            {hotelData
                              ? renderText(
                                  `${counts?.adult} Adult, ${counts?.child} Child, ${counts?.infant} Infant`,
                                  90
                                )
                              : null}
                          </Box>
                          {membersCount > 0 && (
                            <DotSeparatorIcon style={{ margin: '0 6px' }} />
                          )}
                          {membersCount > 0 && (
                            <Box display='flex' alignItems='center' gap='4px'>
                              <MembersIcon />
                              <Box display='flex' alignItems='center' gap='4px'>
                                {hotelData ? renderText(memberName, 120) : null}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <div
                        id='orderTabForScroll'
                        className={classNames('d-flex overflow-auto', 'order-view--tabs')}>
                        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-7 fw-bolder flex-nowrap'>
                          {getListItem('Activity log', 'activity-log')}
                          {getListItem('Travel details', 'travel-details')}
                          {membersCount > 0 &&
                            getListItem('Traveller info', 'traveller-info')}
                          {hotelData?.bookingStatus !== 'ITINERARY' &&
                            getListItem('Payment summary', 'payment-summary')}
                        </ul>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md='8'>
                  <Routes>
                    <Route path='/' element={<Navigate to='activity-log' replace />} />
                    <Route
                      path='activity-log'
                      element={
                        hotelData ? (
                          <ActivityLogs data={hotelData} />
                        ) : (
                          <>
                            <Skeleton
                              variant='rounded'
                              width='100%'
                              height={24}
                              sx={{ mb: '26px' }}
                            />
                            <Skeleton
                              variant='rounded'
                              width='100%'
                              height={24}
                              sx={{ mb: '26px' }}
                            />
                            <Skeleton
                              variant='rounded'
                              width='100%'
                              height={24}
                              sx={{ mb: '26px' }}
                            />
                            <Skeleton
                              variant='rounded'
                              width='100%'
                              height={24}
                              sx={{ mb: '26px' }}
                            />
                            <Skeleton
                              variant='rounded'
                              width='100%'
                              height={24}
                              sx={{ mb: '26px' }}
                            />
                          </>
                        )
                      }
                    />
                    <Route path='travel-details' element={<HotelDetail data={hotelData} />} />
                    {membersCount > 0 && (
                      <Route
                        path='traveller-info'
                        element={
                          <TravellerInfo
                            data={hotelData}
                            handleSelected={val => setSelected(val)}
                            selected={selected}
                          />
                        }
                      />
                    )}
                    <Route
                      path='payment-summary'
                      element={<PaymentSummary data={hotelData} modalOpen={isCancelModalOpen} />}
                    />
                  </Routes>
                </Col>
                <Col md='4'>
                  {!paymentStatus && (
                    <CommentsAndTasks
                      orderData={hotelData}
                      currentUser={currentUser?.data}
                      isFlight={true}
                    />
                  )}
                  {paymentStatus && <PaymentStatus data={hotelData} />}{' '}
                </Col>
              </Row>
              {/**
               * DEVNOTE: keep all modal component at end for better readability...
               */}
            </>
          }>
          <Route path='*' element={<Navigate to='/error/404' />} />
        </Route>
      </Routes>
      <HotelOrderStatus
        isOpen={isStatusUpdateOpen}
        onHide={() => setIsStatusUpdateOpen(false)}
        data={hotelData}
      />
      {hotelData?.bookingStatus !== 'ITINERARY' && (
        <CancelHotelBooking
          setIsCancelModalOpen={setIsCancelModalOpen}
          isCancelModalOpen={isCancelModalOpen}
          data={hotelData}
        />
      )}
    </>
  );
};
